import { LatestQuickView, quickViewURL, needsProductPage, initialiseQuickView, setQuickViewImage, variantImageIndex, prepareQuickViewReturnFocus } from './sf-quick-view.js?v=ea4e8cb0b2d9c0d2e026d593f8d67fba';
import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || this.dataset.productUrl || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    // This compact layout adapts in place; resizing must preserve the selected product.
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();


    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    // Only flip choose <-> add when both buttons were rendered.
    // Otherwise the flip would hide the sole rendered button and reveal nothing.
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    let quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';

    // A single-option card can resolve to an unavailable variant (e.g. re-selecting a
    // sold-out swatch). Keep "Choose" so shoppers reach the picker, not a dead-end disabled "Add".
    if (quickAddButton === 'add' && this.#isSelectedVariantUnavailable()) {
      quickAddButton = 'choose';
    }

    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Whether the card's currently selected swatch maps to an unavailable variant.
   * Reads `data-option-available` off the variant picker's selected option - the same
   * signal the product card uses. Only reports true on an explicit `false`, so an
   * unknown/absent signal leaves the caller's default ("add") untouched.
   * @returns {boolean}
   */
  #isSelectedVariantUnavailable() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.variantPicker?.selectedOption?.dataset.optionAvailable === 'false';
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();
    const dialog = document.getElementById('quick-add-dialog');
    if (!(dialog instanceof QuickAddDialog)) {
      if (this.productPageUrl) window.location.href = this.productPageUrl;
      return;
    }
    // Some touch browsers do not focus clicked buttons; seed the native dialog return target.
    const trigger = event.target instanceof Element ? event.target.closest('button') : null;
    trigger?.focus({ preventScroll: true });
    prepareQuickViewReturnFocus(dialog.refs.dialog, trigger, event);
    dialog.loadProduct(this.productPageUrl);
    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();
  #request = new LatestQuickView();
  #lastUrl = '';
  #galleryCleanup;
  #selection = 0;

  loadProduct = (path) => {
    this.#galleryCleanup?.();
    this.#galleryCleanup = null;
    this.#lastUrl = path;
    this.#selection++;
    const content = this.querySelector('#quick-add-modal-content');
    const status = this.querySelector('[data-qv-status]');
    content.replaceChildren();
    content.hidden = true;
    this.refs.dialog.setAttribute('aria-busy', 'true');
    status.hidden = false;
    status.querySelector('[data-qv-loading]').hidden = false;
    status.querySelector('[data-qv-error]').hidden = true;
    return this.#request.run(async (signal) => {
      const sectionUrl = quickViewURL(path, window.location.origin, this.dataset.previewThemeId);
      const pageUrl = new URL(sectionUrl);
      pageUrl.searchParams.delete('section_id');
      const responses = await Promise.all([fetch(sectionUrl, {signal}), fetch(pageUrl, {signal})]);
      if (responses.some(response => !response.ok)) throw new Error(`HTTP ${responses.map(response => response.status).join('/')}`);
      const texts = await Promise.all(responses.map(response => response.text()));
      const parser = new DOMParser();
      const html = parser.parseFromString(texts[0], 'text/html');
      const fullPage = parser.parseFromString(texts[1], 'text/html');
      const root = html.querySelector('[data-sf-quick-view]');
      if (!root) throw new Error('Quick view unavailable');
      if (root.dataset.fullPage === 'true' || needsProductPage(fullPage)) {
        root.querySelectorAll('[data-qv-purchase]').forEach(element => element.remove());
        root.querySelectorAll('[data-qv-personalise]').forEach(element => { element.hidden = false; });
      }
      return root;
    }, root => {
      content.replaceChildren(root);
      content.hidden = false;
      status.hidden = true;
      this.refs.dialog.setAttribute('aria-busy', 'false');
      this.#galleryCleanup = initialiseQuickView(root);
    }, error => {
      status.dataset.qvError = error.message;
      content.replaceChildren();
      content.hidden = true;
      status.querySelector('[data-qv-loading]').hidden = true;
      status.querySelector('[data-qv-error]').hidden = false;
      this.refs.dialog.setAttribute('aria-busy', 'false');
      const link = status.querySelector('[data-qv-error-link]');
      try {
        const url = new URL(quickViewURL(path, window.location.origin));
        url.searchParams.delete('section_id');
        link.href = url.href;
        link.hidden = false;
      } catch { link.hidden = true; }
    });
  };

  retry = () => this.loadProduct(this.#lastUrl);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.querySelector('[data-qv-retry]')?.addEventListener('click', this.retry, {signal:this.#abortController.signal});
    this.refs.dialog.addEventListener('close', () => { this.#request.cancel(); this.#selection++; }, {signal:this.#abortController.signal});
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#request.cancel();
    this.#galleryCleanup?.();
    this.#abortController.abort();
    this.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    const generation = ++this.#selection;
    const root = this.querySelector('[data-sf-quick-view]');
    event.promise?.then(({detail}) => {
      if (!detail?.html || generation !== this.#selection || !root?.isConnected) return;
      const variant = detail.resource;
      const nextLink = detail.html.querySelector('[data-qv-product-link]');
      if (nextLink) root.querySelector('[data-qv-product-link]').href = nextLink.href;
      const slides = [...root.querySelectorAll('[data-qv-image]')];
      const index = variantImageIndex(variant, slides.map(slide => slide.dataset.mediaId));
      if (index >= 0) setQuickViewImage(root, index);
    }).catch(error => {
      if (error?.name !== 'AbortError') console.warn('[quick-view] Variant update failed');
    });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
